import React, { useState, useEffect } from 'react';
import api from '../api';
import logo from '../images/logo-rr-news.png';
import { generateConsentLetterPDF } from '../utils/generateConsentLetter';
import './ConsentForm.css';

function ConsentForm({ onClose, onSuccess, onFormDataSaved, userProfile }) {
  const [formData, setFormData] = useState({
    name: '',
    fatherName: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    bloodGroup: '',
    mobileNumber: userProfile?.email || '',
    alternateMobile: '',
    email: userProfile?.email || '',
    address: userProfile?.address || '',
    reporterRole: userProfile?.pressRole || '',
    qualification: '',
    profession: '',
    appointmentDate: '',
    pressCardDate: userProfile?.approvedAt ? new Date(userProfile.approvedAt).toISOString().split('T')[0] : '',
    photo: null,
    signature: null,
  });
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Check if user has already submitted consent form on component mount
  useEffect(() => {
    const checkExistingConsent = async () => {
      try {
        // Clear any stale localStorage data from previous users
        localStorage.removeItem('consentFormData');
        
        const token = localStorage.getItem('token');
        if (token) {
          const res = await api.get('/api/users/me', {
            headers: { 'Authorization': 'Bearer ' + token }
          });
          
          // Check if user has consent form data using isConsent field
          if (res.data?.data?.isConsent === true) {
            setFormSubmitted(true);
            
            // Load existing consent data from backend only
            const consentData = res.data.data.consentData;
            if (consentData) {
              setFormData(prev => ({
                ...prev,
                name: consentData.name || prev.name,
                fatherName: consentData.fatherName || '',
                dateOfBirth: consentData.dateOfBirth || '',
                gender: consentData.gender || '',
                maritalStatus: consentData.maritalStatus || '',
                bloodGroup: consentData.bloodGroup || '',
                mobileNumber: consentData.mobileNumber || prev.mobileNumber,
                alternateMobile: consentData.alternateMobile || '',
                email: consentData.email || prev.email,
                address: consentData.address || prev.address,
                reporterRole: consentData.reporterRole || prev.reporterRole,
                qualification: consentData.qualification || '',
                profession: consentData.profession || '',
                appointmentDate: consentData.appointmentDate || '',
                pressCardDate: consentData.pressCardDate || prev.pressCardDate,
                photo: consentData.photo || null,
                signature: consentData.signature || null,
              }));
            }
          } else {
            // User has not submitted consent form, ensure formSubmitted is false
            setFormSubmitted(false);
          }
        }
      } catch (err) {
        console.warn('Failed to check existing consent form:', err);
        setFormSubmitted(false);
      }
    };
    
    checkExistingConsent();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, photo: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSignatureFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({ ...prev, signature: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.fatherName || !formData.mobileNumber || !formData.email) {
      alert('कृपया सभी आवश्यक क्षेत्र भरें');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.post('/api/users/consent-form', formData, {
        headers: { 'Authorization': 'Bearer ' + token }
      });

      if (res.data && res.data.success) {
        alert('सहमति फॉर्म सफलतापूर्वक जमा किया गया');
        setFormSubmitted(true);
        onFormDataSaved?.(formData);
        onSuccess?.();
      } else {
        alert(res.data?.message || 'फॉर्म सबमिशन विफल');
      }
    } catch (err) {
      alert('त्रुटि: ' + (err.message || 'सबमिशन विफल'));
    } finally {
      setLoading(false);
    }
  };

  const handleGetLetter = async () => {
    // Ensure consent form is submitted before allowing letter download
    if (!formSubmitted) {
      alert('कृपया पहले सहमति फॉर्म जमा करें।');
      return;
    }

    if (!formData.name || !formData.fatherName || !formData.mobileNumber || !formData.email || !formData.address) {
      alert('कृपया सभी आवश्यक जानकारी भरें: नाम, पिता का नाम, मोबाइल नंबर, ईमेल और पता');
      return;
    }

    setGeneratingPDF(true);
    try {
      // Fetch latest profile to get current role, ID, region, and approval date
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: 'Bearer ' + token } : {};
      let latestProfile = userProfile;
      
      try {
        const profileRes = await api.get('/api/users/me', { headers });
        if (profileRes?.data?.data) {
          latestProfile = profileRes.data.data;
        }
      } catch (e) {
        console.warn('Failed to fetch fresh profile:', e);
      }

      // Extract role, ID, region, and approval date from profile
      const reporterRole = latestProfile?.pressRole?.trim() || latestProfile?.role?.trim() || formData.reporterRole?.trim() || 'CORRESPONDENT';
      const reporterId = latestProfile?.reporterId?.trim() || null;
      const reporterRegion = latestProfile?.region?.trim() || null;
      const approvedAt = latestProfile?.approvedAt || null;
      
      const result = await generateConsentLetterPDF(formData, formData.name || userProfile?.name, reporterRole, reporterId, reporterRegion, approvedAt);
      if (result.success) {
        alert(`पत्र सफलतापूर्वक डाउनलोड किया गया!\n\nअनूठा ID: ${result.uniqueId}\nवैधता: ${result.validFrom} से ${result.validUpto} तक`);
      }
    } catch (error) {
      alert('पत्र जनरेशन विफल: ' + error.message);
    } finally {
      setGeneratingPDF(false);
    }
  };

  return (
    <div className="consent-form-overlay">
      <div className="consent-form-container">
        <div className="consent-form-close" onClick={onClose}>✕</div>
        
        <div className="consent-form-header">
          <img src={logo} alt="RR News Logo" className="consent-logo" />
          <h3 className="consent-org-name">RR NEWS TV FASTEST UPDATE</h3>
          <h2 className="consent-form-title">पत्रकारिता टर्म्स एंड कंडीशन्स फ़ॉर्म</h2>
          <p className="consent-reg-id"><strong>UDYAM-UP-59-0118348</strong></p>
        </div>

        <form onSubmit={handleSubmit} className="consent-form">
          <div className="form-section">
            <h4>व्यक्तिगत विवरण</h4>
            
            <div className="form-row">
              <div className="form-group">
                <label>नाम *</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="नाम" required />
              </div>
              <div className="form-group">
                <label>पिता का नाम *</label>
                <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} placeholder="पिता का नाम" required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>जन्म तिथि</label>
                <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>लिंग</label>
                <select name="gender" value={formData.gender} onChange={handleChange}>
                  <option value="">चुनें</option>
                  <option value="पुरुष">पुरुष</option>
                  <option value="महिला">महिला</option>
                  <option value="अन्य">अन्य</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>वैवाहिक स्थिति</label>
                <select name="maritalStatus" value={formData.maritalStatus} onChange={handleChange}>
                  <option value="">चुनें</option>
                  <option value="विवाहित">विवाहित</option>
                  <option value="अविवाहित">अविवाहित</option>
                  <option value="अन्य">अन्य</option>
                </select>
              </div>
              <div className="form-group">
                <label>रक्त समूह</label>
                <select name="bloodGroup" value={formData.bloodGroup} onChange={handleChange}>
                  <option value="">चुनें</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>मोबाइल नंबर *</label>
                <input type="tel" name="mobileNumber" value={formData.mobileNumber} onChange={handleChange} placeholder="मोबाइल नंबर" required />
              </div>
              <div className="form-group">
                <label>वैकल्पिक नंबर</label>
                <input type="tel" name="alternateMobile" value={formData.alternateMobile} onChange={handleChange} placeholder="वैकल्पिक नंबर" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>ई-मेल *</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="ई-मेल" required />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>पता</label>
                <textarea name="address" value={formData.address} onChange={handleChange} placeholder="पूरा पता" rows="2"></textarea>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>पत्रकार की भूमिका</label>
                <input type="text" name="reporterRole" value={formData.reporterRole} onChange={handleChange} placeholder="पत्रकार की भूमिका" />
              </div>
              <div className="form-group">
                <label>योग्यता</label>
                <input type="text" name="qualification" value={formData.qualification} onChange={handleChange} placeholder="योग्यता" />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>व्यवसाय</label>
                <input type="text" name="profession" value={formData.profession} onChange={handleChange} placeholder="व्यवसाय" />
              </div>
              <div className="form-group">
                <label>संस्था में नियुक्ति तिथि</label>
                <input type="date" name="appointmentDate" value={formData.appointmentDate} onChange={handleChange} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>प्रेस कार्ड दिनांक</label>
                <input type="date" name="pressCardDate" value={formData.pressCardDate} onChange={handleChange} readOnly disabled />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>पत्रकार की फोटो</label>
                <input type="file" accept="image/*" onChange={handlePhotoChange} />
                {formData.photo && (
                  <img src={formData.photo} alt="Reporter Photo" className="photo-preview" />
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label>आवेदक के हस्ताक्षर</label>
                <input type="file" accept="image/*" onChange={handleSignatureChange} />
                {formData.signature && (
                  <img src={formData.signature} alt="Signature" className="signature-preview" />
                )}
              </div>
            </div>
          </div>

          <div className="form-section terms-section">
            <h4>नियम एवं शर्तें</h4>
            <div className="terms-content">
              <p><strong>(1)</strong> यह कार्ड आपको संविधान के अंतर्गत (प्रेस एंड रजिस्ट्रेशन ऑफ़ बुक्स एक्ट, 1867) के अनुसार मीडिया के नियमों का पालन करते हुए प्रदान किया जाता है।</p>
              
              <p><strong>(2)</strong> यह कार्ड आपको मीडिया में ईमानदारी, सत्यनिष्ठा के साथ कार्य करने, जनता की आवाज़ बनने और उनकी ताक़त बनने के उद्देश्य से प्रदान किया जाता है।</p>
              
              <p><strong>(3)</strong> यह कार्ड आपको पूर्ण स्वतंत्रता प्रदान करता है। आप किसी भी स्तर के पदाधिकारी, अधिकारी अथवा जनप्रतिनिधि से प्रश्न कर सकते हैं और उनसे जवाब प्राप्त कर सकते हैं।</p>
              
              <p><strong>(4)</strong> यह कार्ड सामाजिक सेवा के उद्देश्य से प्रदान किया गया है। किसी भी निजी कार्य में संस्था की कोई भूमिका नहीं होगी।</p>
              
              <p><strong>(5)</strong> इस कार्ड का दुरुपयोग किए जाने पर आपका कार्ड तत्काल प्रभाव से निरस्त कर दिया जाएगा तथा संस्था की छवि धूमिल होने की स्थिति में आपके विरुद्ध वैधानिक कार्रवाई की जाएगी।</p>
              
              <p><strong>(6)</strong> किसी भी गैर-संवैधानिक या गैर-कानूनी गतिविधि में संलिप्त पाए जाने पर उसकी पूर्ण जिम्मेदारी आपकी स्वयं की होगी। संस्था का उससे कोई लेना-देना नहीं होगा।</p>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={loading || generatingPDF}>रद्द करें</button>
            {!formSubmitted ? (
              <button type="submit" className="btn-submit" disabled={loading || generatingPDF}>
                {loading ? 'जमा किया जा रहा है...' : 'सहमति के साथ जमा करें'}
              </button>
            ) : (
              <>
                <button 
                  type="button" 
                  className="btn-get-letter" 
                  onClick={handleGetLetter}
                  disabled={generatingPDF}
                >
                  {generatingPDF ? 'पत्र बन रहा है...' : '📥 पत्र डाउनलोड करें'}
                </button>
                <button type="button" className="btn-close-form" onClick={onClose} disabled={generatingPDF}>
                  बंद करें
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConsentForm;


